export const calculateStatistics = (data, columns) => {
  if (!data || data.length === 0) {
    return {
      totalValue: 0,
      minYear: null,
      maxYear: null,
      yearRange: 'N/A',
      availableYears: []
    };
  }

  // Buscar coluna PROCESSO (para emendas estaduais) ou EMENDA (para municipais)
  const processoColumn = columns.find(col => 
    col.header.toLowerCase().includes('processo')
  );
  const emendaColumn = columns.find(col => 
    col.header.toLowerCase().includes('emenda')
  );
  
  // Priorizar PROCESSO se existir (emendas estaduais)
  const yearColumn = processoColumn || emendaColumn;
  const isProcessoColumn = !!processoColumn;

  // Encontrar a coluna VALOR (busca case-insensitive e trim)
  const valueColumn = columns.find(col => {
    const header = col.header.trim().toLowerCase();
    return header === 'valor' || 
           header.includes('valor') || 
           header.includes('vlr');
  });

  // Calcular valor total
  let totalValue = 0;
  if (valueColumn) {
    totalValue = data.reduce((sum, row) => {
      const value = row[valueColumn.accessorKey];
      const numericValue = parseValueToNumber(value);
      return sum + numericValue;
    }, 0);
  }

  // Encontrar anos mínimo e máximo
  let minYear = null;
  let maxYear = null;
  const yearCountMap = new Map(); // Mapa: ano -> quantidade
  
  if (yearColumn) {
    data.forEach(row => {
      const value = row[yearColumn.accessorKey];
      const year = extractYearFromValue(value, isProcessoColumn);
      if (year !== null) {
        yearCountMap.set(year, (yearCountMap.get(year) || 0) + 1);
      }
    });

    const years = Array.from(yearCountMap.keys()).sort((a, b) => a - b);
    
    if (years.length > 0) {
      minYear = years[0];
      maxYear = years[years.length - 1];
    }
  }

  const yearRange = minYear && maxYear 
    ? minYear === maxYear 
      ? `${minYear}` 
      : `${minYear} - ${maxYear}`
    : 'N/A';

  // Criar array de anos com contagem, ordenado decrescente
  const yearBreakdown = Array.from(yearCountMap.entries())
    .map(([year, count]) => ({ year, count }))
    .sort((a, b) => b.year - a.year);

  const availableYears = yearBreakdown.map(item => item.year);

  return {
    totalValue,
    totalCount: data.length,
    minYear,
    maxYear,
    yearRange,
    availableYears,
    yearBreakdown
  };
};

const extractYearFromValue = (value, isProcessoColumn) => {
  if (!value) return null;
  
  const valueStr = String(value).trim();
  
  if (isProcessoColumn) {
    // Emendas Estaduais: extrair ano dos primeiros 4 dígitos da coluna PROCESSO
    // Formato: "2025/TR001836" ou "2025TR001836" -> extrai 2025
    const firstFourDigits = valueStr.substring(0, 4);
    const year = parseInt(firstFourDigits);
    
    if (!isNaN(year) && year >= 1900 && year <= 2100) {
      return year;
    }
  } else {
    // Emendas Municipais: extrair ano após a barra da coluna EMENDA
    // Formato: "0001/2024" -> extrai 2024
    const parts = valueStr.split('/');
    
    if (parts.length >= 2) {
      const lastPart = parts[parts.length - 1].trim();
      const year = parseInt(lastPart);
      
      if (!isNaN(year) && year >= 1900 && year <= 2100) {
        return year;
      }
    }
  }
  
  return null;
};

const parseValueToNumber = (value) => {
  if (!value) return 0;
  
  // Converter para string e fazer trim
  let strValue = String(value).trim();
  
  // Se já for um número, retornar diretamente
  if (typeof value === 'number') {
    return value;
  }
  
  // Remove R$, cifrão, espaços
  strValue = strValue
    .replace(/R\$/gi, '')
    .replace(/\$/g, '')
    .replace(/\s+/g, '');
  
  // Detectar formato: verificar posição da vírgula e do ponto
  const hasComma = strValue.includes(',');
  const hasDot = strValue.includes('.');
  
  if (hasComma && hasDot) {
    // Tem ambos: verificar qual vem por último (é o decimal)
    const lastCommaPos = strValue.lastIndexOf(',');
    const lastDotPos = strValue.lastIndexOf('.');
    
    if (lastDotPos > lastCommaPos) {
      // Formato americano: 1,234,567.89
      // Remove vírgulas (separadores de milhares)
      strValue = strValue.replace(/,/g, '');
    } else {
      // Formato brasileiro: 1.234.567,89
      // Remove pontos e troca vírgula por ponto
      strValue = strValue.replace(/\./g, '').replace(',', '.');
    }
  } else if (hasComma && !hasDot) {
    // Apenas vírgula: verificar quantos dígitos após a vírgula
    const parts = strValue.split(',');
    if (parts.length === 2 && parts[1].length === 2) {
      // Formato brasileiro decimal: 1234,56
      strValue = strValue.replace(',', '.');
    } else {
      // Formato americano milhares: 1,234 ou 40,000
      strValue = strValue.replace(/,/g, '');
    }
  } else if (!hasComma && hasDot) {
    // Apenas ponto: verificar se é decimal ou milhares
    const parts = strValue.split('.');
    if (parts.length === 2 && parts[1].length === 2) {
      // É decimal: 1234.56
      // Mantém como está
    } else {
      // É separador de milhares: 1.234
      strValue = strValue.replace(/\./g, '');
    }
  }
  
  const number = parseFloat(strValue);
  return isNaN(number) ? 0 : number;
};

const parseYearToNumber = (value) => {
  if (!value) return null;
  
  const year = parseInt(String(value).trim());
  
  // Validar se é um ano válido (entre 1900 e 2100)
  if (isNaN(year) || year < 1900 || year > 2100) {
    return null;
  }
  
  return year;
};

/**
 * Calcula estatísticas unificadas considerando anos de ambos os tipos de emendas
 * @param {Array} currentData - Dados da aba atual
 * @param {Array} currentColumns - Colunas da aba atual
 * @param {Array} otherData - Dados da outra aba
 * @param {Array} otherColumns - Colunas da outra aba
 * @returns {Object} Estatísticas com anos unificados
 */
export const calculateUnifiedStatistics = (currentData, currentColumns, otherData, otherColumns) => {
  // Calcular estatísticas da aba atual (valor e contagem)
  const currentStats = calculateStatistics(currentData, currentColumns);
  
  if (!otherData || otherData.length === 0) {
    // Se não há dados da outra aba, retornar estatísticas normais
    return currentStats;
  }
  
  // Extrair anos da aba atual
  const currentYearMap = new Map();
  const processoColumn = currentColumns.find(col => col.header.toLowerCase().includes('processo'));
  const emendaColumn = currentColumns.find(col => col.header.toLowerCase().includes('emenda'));
  const currentYearColumn = processoColumn || emendaColumn;
  const isCurrentProcesso = !!processoColumn;
  
  if (currentYearColumn) {
    currentData.forEach(row => {
      const value = row[currentYearColumn.accessorKey];
      const year = extractYearFromValue(value, isCurrentProcesso);
      if (year !== null) {
        currentYearMap.set(year, (currentYearMap.get(year) || 0) + 1);
      }
    });
  }
  
  // Extrair anos da outra aba
  const otherYearMap = new Map();
  const otherProcessoColumn = otherColumns.find(col => col.header.toLowerCase().includes('processo'));
  const otherEmendaColumn = otherColumns.find(col => col.header.toLowerCase().includes('emenda'));
  const otherYearColumn = otherProcessoColumn || otherEmendaColumn;
  const isOtherProcesso = !!otherProcessoColumn;
  
  if (otherYearColumn) {
    otherData.forEach(row => {
      const value = row[otherYearColumn.accessorKey];
      const year = extractYearFromValue(value, isOtherProcesso);
      if (year !== null) {
        otherYearMap.set(year, (otherYearMap.get(year) || 0) + 1);
      }
    });
  }
  
  // Unificar todos os anos (de ambas as abas)
  const allYears = new Set([
    ...Array.from(currentYearMap.keys()),
    ...Array.from(otherYearMap.keys())
  ]);
  
  // Criar breakdown unificado com contagem zero para anos ausentes na aba atual
  const unifiedYearBreakdown = Array.from(allYears)
    .map(year => ({
      year,
      count: currentYearMap.get(year) || 0
    }))
    .sort((a, b) => b.year - a.year);
  
  // Calcular range unificado
  const sortedYears = Array.from(allYears).sort((a, b) => a - b);
  const minYear = sortedYears.length > 0 ? sortedYears[0] : null;
  const maxYear = sortedYears.length > 0 ? sortedYears[sortedYears.length - 1] : null;
  
  const yearRange = minYear && maxYear
    ? minYear === maxYear
      ? `${minYear}`
      : `${minYear} - ${maxYear}`
    : 'N/A';
  
  return {
    ...currentStats,
    yearRange,
    yearBreakdown: unifiedYearBreakdown,
    minYear,
    maxYear,
    availableYears: sortedYears
  };
};

export const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};
